import { ArrowHeadRightIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { StudentMapping, XlsxImportEntry } from '../types/XlsxImportTypes';
import { getLabelFromMapping } from '../utils/XlsxImportUtils';
import styles from './XlsxImportOverview.module.scss';

type XlsxImportOverviewProps<T extends StudentMapping> = {
  entries: XlsxImportEntry<T>[];
  mandatoryFields: T[];
  notFoundFields: T[];
};

export const XlsxImportOverview = <T extends StudentMapping>({
  entries,
  mandatoryFields,
  notFoundFields,
}: XlsxImportOverviewProps<T>) => {
  const { t } = useTranslation();

  const importEntries = entries.filter((e) => e.ignore.length === 0);
  const ignoredEntries = entries.filter((e) => e.ignore.length !== 0);

  const fields = [...mandatoryFields, ...notFoundFields];

  const fieldSummary = fields.reduce(
    (acc, field) => {
      acc[field] = ignoredEntries.filter((entry) => entry.ignore.includes(field));
      return acc;
    },
    {} as Record<T, XlsxImportEntry<T>[]>,
  );

  const hasIgnored = fields.some((m) => fieldSummary[m].length > 0);

  return (
    <div className={styles['xlsx-import-overview']}>
      <div className={styles.summary}>
        <div className={classNames(styles.import, { [styles.solo]: !hasIgnored })}>
          <div className={styles.icon}>
            <ArrowHeadRightIcon />
          </div>
          <div className={styles.text}>
            {t('import.importOverview', {
              count: importEntries.length,
              type: t('students.title', { count: importEntries.length }),
            })}
          </div>
        </div>
        {hasIgnored && (
          <div className={styles.ignore}>
            <div className={styles.icon}>
              <ArrowHeadRightIcon />
            </div>
            <div className={styles.text}>{t('import.ignoreOverview', { count: ignoredEntries.length })}:</div>
          </div>
        )}
      </div>
      {hasIgnored && (
        <>
          <div className={styles.mandatory}>
            {mandatoryFields.map((field) => {
              if (fieldSummary[field].length > 0) {
                return (
                  <div key={field} className={styles.field}>
                    <div className={styles.headline}>
                      {t('import.missingField', { field: getLabelFromMapping(field) })}
                    </div>
                    <table>
                      <tbody>
                        {fieldSummary[field].map((entry) => {
                          return (
                            <tr key={entry.rowIndex} className={styles.row}>
                              {fields.map((entryField) => {
                                return (
                                  <td
                                    key={`${field}-${entry.rowIndex}-${entryField}`}
                                    className={entry[entryField] === null ? styles.empty : undefined}
                                  >
                                    {entry[entryField]}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              }
            })}
          </div>
          <div className={styles['not-found']}>
            {notFoundFields.map((field) => {
              if (fieldSummary[field].length > 0) {
                return (
                  <div key={field} className={styles.field}>
                    <div className={styles.headline}>
                      {t('import.notFoundField', { field: getLabelFromMapping(field) })}
                    </div>
                    <table>
                      <tbody>
                        {fieldSummary[field].map((entry) => {
                          return (
                            <tr key={entry.rowIndex} className={styles.row}>
                              {fields.map((entryField) => {
                                return (
                                  <td
                                    key={`${field}-${entry.rowIndex}-${entryField}`}
                                    className={entry[entryField] === null ? styles.empty : undefined}
                                  >
                                    {entry[entryField]}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              }
            })}
          </div>
        </>
      )}
    </div>
  );
};
